<!--
  McpIntegration.vue

  Per-package screen for a vetted MCP package (catalog in useMcpClients),
  mirroring the Membrane integration screen (MembraneIntegration →
  IntegrationSyncWorkflow): header + description, an empty phase that collects
  the package's credentials and connects, and a ready phase showing the
  configured server with enable / test / edit / remove actions.

  External MCP servers run as local processes, so the web build shows a
  "get the desktop app" call to action instead.
-->
<template>
  <div v-if="pkg" class="settings-page integration">
    <div class="title">
      <div>
        <img v-if="pkg.logoUri" :src="pkg.logoUri" class="header-icon" />
        <h2>{{ pkg.name }}</h2>
        <button
          v-if="pkg.docsUrl"
          type="button"
          class="docs-info"
          aria-label="Open documentation"
          v-tippy="{ content: 'Documentation', arrow: false, placement: 'top' }"
          @click="openUrl(pkg.docsUrl)"
        >
          <InfoIcon />
        </button>
      </div>
      <span
        ref="mcpTagEl"
        class="mcp-tag"
        @mouseenter="showMcpTooltip"
        @mouseleave="hideMcpTooltip"
        >MCP</span
      >
    </div>

    <p class="description">{{ pkg.description }}</p>

    <div v-if="!supported" class="alert-container">
      <DownloadButton
        kind="main"
        type="secondary"
        text="Get the desktop app"
        :message="`${pkg.name} runs as a local MCP server, which is only supported in the desktop apps.`"
      />
    </div>

    <template v-else-if="!loading">
      <!-- Empty: no server configured yet -->
      <section v-if="!server" class="workflow-empty">
        <div class="workflow-empty-card">
          <div class="workflow-empty-logo-wrap">
            <img
              v-if="pkg.logoUri"
              :src="pkg.logoUri"
              class="workflow-empty-logo"
            />
          </div>
          <div class="workflow-empty-body">
            <p class="workflow-empty-headline">
              No {{ pkg.name }} server connected
            </p>
            <p class="workflow-empty-sub">
              {{
                fields.length > 0
                  ? "Enter your credentials to make its tools available to Routine's AI."
                  : "Connect it to make its tools available to Routine's AI."
              }}
            </p>
          </div>

          <form
            v-if="fields.length > 0 || trustNote"
            class="mcp-form"
            @submit.prevent="connect"
          >
            <label v-for="field in fields" :key="field.key">
              <span
                >{{ field.label
                }}<span v-if="!field.required" class="optional">
                  (optional)</span
                ></span
              >
              <input
                v-model="values[field.key]"
                :type="field.secret ? 'password' : 'text'"
                :placeholder="field.placeholder"
              />
              <span v-if="field.description" class="hint">{{
                field.description
              }}</span>
            </label>
            <p v-if="trustNote" class="hint">{{ trustNote }}</p>
          </form>

          <BaseButton
            kind="main"
            type="primary"
            :text="`Connect ${pkg.name}`"
            :state="saving ? 'loading' : canSubmit ? 'default' : 'disabled'"
            @click="connect"
          />
          <p v-if="errorText" class="form-error">{{ errorText }}</p>
        </div>
      </section>

      <!-- Ready: the configured server -->
      <section v-else class="workflow-ready">
        <h3>Server</h3>
        <div class="workflow-account">
          <div class="workflow-ready-row">
            <div class="workflow-ready-text">
              <img
                v-if="pkg.logoUri"
                :src="pkg.logoUri"
                class="workflow-ready-icon"
              />
              <div class="workflow-ready-name">{{ server.name }}</div>
              <span
                class="workflow-ready-status-dot"
                :class="status.kind"
                v-tippy="{
                  content: status.tooltip,
                  arrow: false,
                  placement: 'top',
                }"
              />
              <div class="trust-pill" :class="server.trust">
                {{ server.trust === "all" ? "All tools" : "Read-only tools" }}
              </div>
            </div>

            <div class="workflow-ready-actions">
              <BaseToggleButton
                :checked="server.enabled"
                label="Enabled"
                :disabled="busy"
                @change="setEnabled"
              />
              <ButtonMenu :offset="[0, 4]" placement="bottom-end">
                <template #btn>
                  <BaseButton kind="auxiliary">
                    <template #icon><MoreIcon /></template>
                  </BaseButton>
                </template>
                <template #content>
                  <BaseButton
                    class="context-action"
                    kind="auxiliary"
                    text="Test connection"
                    is-expanded
                    @click="test"
                  >
                    <template #icon><RefreshIcon /></template>
                  </BaseButton>
                  <BaseButton
                    class="context-action"
                    kind="auxiliary"
                    text="Edit configuration"
                    is-expanded
                    @click="startEdit"
                  >
                    <template #icon><EditIcon /></template>
                  </BaseButton>
                  <BaseButton
                    class="context-action"
                    kind="auxiliary"
                    text="Remove"
                    tint="red"
                    is-expanded
                    @click="remove"
                  >
                    <template #icon><DeleteIcon /></template>
                  </BaseButton>
                </template>
              </ButtonMenu>
            </div>
          </div>

          <p v-if="toolsSummary" class="mcp-tools">{{ toolsSummary }}</p>
          <p v-if="errorText" class="form-error">{{ errorText }}</p>

          <form v-if="editing" class="mcp-form" @submit.prevent="save">
            <label v-for="field in fields" :key="field.key">
              <span
                >{{ field.label
                }}<span v-if="!field.required" class="optional">
                  (optional)</span
                ></span
              >
              <input
                v-model="values[field.key]"
                :type="field.secret ? 'password' : 'text'"
                :placeholder="field.placeholder"
              />
              <span v-if="field.description" class="hint">{{
                field.description
              }}</span>
            </label>
            <label>
              <span>Trust</span>
              <select v-model="trust">
                <option value="readOnly">Read-only tools (recommended)</option>
                <option value="all">All tools (can modify data & act)</option>
              </select>
            </label>
            <p class="hint">
              Read-only exposes only tools the server marks as non-modifying —
              the agent physically cannot invoke anything else. Choose "All
              tools" only for servers you fully trust.
            </p>
            <div class="form-actions">
              <BaseButton
                kind="main"
                type="secondary"
                text="Cancel"
                @click="cancelEdit"
              />
              <BaseButton
                kind="main"
                type="primary"
                text="Save & test"
                :state="saving ? 'loading' : canSubmit ? 'default' : 'disabled'"
                @click="save"
              />
            </div>
          </form>
        </div>
      </section>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, ref, watch } from "vue";

import type {
  ExternalMcpServer,
  ExternalMcpStatus,
  ExternalMcpTransport,
  ExternalMcpTrust,
} from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseToggleButton from "@/components/base/BaseToggleButton.vue";
import DownloadButton from "@/components/base/DownloadButton.vue";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import DeleteIcon from "@/components/icons/DeleteIcon.vue";
import EditIcon from "@/components/icons/EditIcon.vue";
import InfoIcon from "@/components/icons/InfoIcon.vue";
import MoreIcon from "@/components/icons/MoreIcon.vue";
import RefreshIcon from "@/components/icons/RefreshIcon.vue";
import * as inject from "@/components/inject";
import { authFromPackage } from "@/components/settings/mcp/types";
import {
  findInstalledServer,
  vettedMcpPackageById,
} from "@/components/settings/mcp/useMcpClients";
import { useTooltip, type TooltipContent } from "@/components/tooltip";
import { RouteNames } from "@/routes/types";
import { in_electron } from "@/utils/in";
import { openExternalUrl } from "@/utils/open-external-url";
import { errorMessage } from "@/utils/unknown";

/** Sentinel `values` key for the HTTP auth credential of remote packages. */
const AUTH_KEY = "__auth__";

/** One credential input in the connect / edit form. */
type CredentialField = {
  key: string;
  label: string;
  required: boolean;
  secret: boolean;
  placeholder?: string;
  description?: string;
};

export default defineComponent({
  components: {
    BaseButton,
    BaseToggleButton,
    ButtonMenu,
    DeleteIcon,
    DownloadButton,
    EditIcon,
    InfoIcon,
    MoreIcon,
    RefreshIcon,
  },
  props: {
    orgId: { type: String, required: true },
    /** Vetted catalog id, from the `:packageId` route param. */
    packageId: { type: String, required: true },
  },
  setup(props) {
    const controller = inject.controller();
    const router = inject.router();
    const integrations = inject.integrations();

    const mcpTagEl = ref<HTMLElement>();
    const mcpTooltipContent = ref<TooltipContent>(
      "MCP configurations are device specific i.e not shared across your devices"
    );
    const {
      showTooltip: showMcpTooltip,
      hideTooltip: hideMcpTooltip,
      release: releaseMcpTooltip,
    } = useTooltip(mcpTagEl, mcpTooltipContent, "top");
    onBeforeUnmount(releaseMcpTooltip);

    const pkg = vettedMcpPackageById(props.packageId) ?? null;

    // Unknown catalog id (bad deep link, package withdrawn): replace-redirect
    // to the integrations list. The catalog is static, so this is immediate.
    if (!pkg) {
      void router.replace({
        name: RouteNames.SettingsIntegrations,
        params: { orgId: props.orgId },
      });
    }

    const supported = in_electron;

    const servers = ref<ExternalMcpServer[]>([]);
    const loading = ref(supported);
    const saving = ref(false);
    const busy = ref(false);
    const editing = ref(false);
    /** Connection error from the last add / update / test probe. */
    const errorText = ref<string | null>(null);

    const server = computed(() =>
      pkg ? (findInstalledServer(servers.value, pkg) ?? null) : null
    );

    const refresh = async () => {
      if (!supported) return;
      servers.value = await controller.externalMcp.list();
      // Keep the app-wide store in step so composer candidates and
      // advertised pills reflect the mutation immediately.
      void integrations.mcp.refresh();
    };

    if (pkg && supported) {
      void refresh().finally(() => {
        loading.value = false;
      });
    }

    // ── Credential form ──────────────────────────────────────────────────

    const fields: CredentialField[] = !pkg
      ? []
      : [
          ...(pkg.auth.kind === "bearer"
            ? [
                {
                  key: AUTH_KEY,
                  label: "Token",
                  required: true,
                  secret: true,
                  placeholder: pkg.auth.placeholder,
                },
              ]
            : pkg.auth.kind === "header"
              ? [
                  {
                    key: AUTH_KEY,
                    label: pkg.auth.name,
                    required: true,
                    secret: true,
                    placeholder: pkg.auth.placeholder,
                  },
                ]
              : []),
          ...(pkg.envVars ?? []).map((envVar) => ({
            key: envVar.name,
            label: envVar.name,
            required: envVar.required ?? true,
            secret: envVar.secret ?? true,
            placeholder: envVar.placeholder,
            description: envVar.description,
          })),
        ];

    const values = ref<Record<string, string>>({});
    const trust = ref<ExternalMcpTrust>(pkg?.defaultTrust ?? "all");
    const trustNote = pkg?.trustNote ?? null;

    const canSubmit = computed(() =>
      fields.every(
        (field) =>
          !field.required || (values.value[field.key] ?? "").trim().length > 0
      )
    );

    // Reset the form whenever the server appears / disappears (connect,
    // remove) so stale secrets never linger in the inputs.
    watch(server, () => {
      editing.value = false;
      values.value = {};
      trust.value = server.value?.trust ?? pkg?.defaultTrust ?? "all";
    });

    /**
     * The package's canonical transport plus the credentials: filled env vars
     * are appended to `env`, and vars with a `cliFlag` are also passed as
     * `[cliFlag, value]` args (some official servers only read the flag).
     */
    const buildTransport = (): ExternalMcpTransport => {
      if (!pkg) return { url: "" };
      const transport = pkg.transport;
      if ("url" in transport) return transport;
      const args = [...(transport.args ?? [])];
      const env = [...(transport.env ?? [])];
      for (const envVar of pkg.envVars ?? []) {
        const value = (values.value[envVar.name] ?? "").trim();
        if (value.length === 0) continue;
        env.push({ name: envVar.name, value });
        if (envVar.cliFlag) args.push(envVar.cliFlag, value);
      }
      return { command: transport.command, args, env };
    };

    const applyStatus = (status: ExternalMcpStatus | null) => {
      errorText.value =
        status === null || status.connection.kind === "connected"
          ? null
          : status.connection.message;
    };

    // ── Actions ──────────────────────────────────────────────────────────

    const openUrl = (url: string) => openExternalUrl(url, controller);

    const connect = async () => {
      if (!pkg || !canSubmit.value || saving.value) return;
      saving.value = true;
      try {
        const status = await controller.externalMcp.add(
          pkg.name,
          buildTransport(),
          authFromPackage(pkg.auth, values.value[AUTH_KEY]),
          trust.value
        );
        applyStatus(status);
        await refresh();
      } catch (error) {
        controller.notifications.notify(errorMessage(error));
      } finally {
        saving.value = false;
      }
    };

    const startEdit = () => {
      const installed = server.value;
      if (!installed) return;
      const transport = installed.transport;
      const env = "command" in transport ? (transport.env ?? []) : [];
      const prefilled: Record<string, string> = {};
      for (const envVar of pkg?.envVars ?? []) {
        const stored = env.find((entry) => entry.name === envVar.name);
        if (stored) prefilled[envVar.name] = stored.value;
      }
      const auth = installed.auth;
      if (auth !== "none") {
        prefilled[AUTH_KEY] = "token" in auth ? auth.token : auth.value;
      }
      values.value = prefilled;
      trust.value = installed.trust;
      editing.value = true;
    };

    const cancelEdit = () => {
      editing.value = false;
      values.value = {};
    };

    const save = async () => {
      const installed = server.value;
      if (!pkg || !installed || !canSubmit.value || saving.value) return;
      saving.value = true;
      try {
        const status = await controller.externalMcp.update(
          installed.id,
          installed.name,
          buildTransport(),
          authFromPackage(pkg.auth, values.value[AUTH_KEY]),
          trust.value
        );
        applyStatus(status);
        editing.value = false;
        values.value = {};
        await refresh();
      } catch (error) {
        controller.notifications.notify(errorMessage(error));
      } finally {
        saving.value = false;
      }
    };

    const withBusy = async (action: () => Promise<void>) => {
      if (busy.value) return;
      busy.value = true;
      try {
        await action();
      } catch (error) {
        controller.notifications.notify(errorMessage(error));
      } finally {
        busy.value = false;
      }
    };

    const setEnabled = (enabled: boolean) =>
      withBusy(async () => {
        const installed = server.value;
        if (!installed) return;
        await controller.externalMcp.setEnabled(installed.id, enabled);
        await refresh();
      });

    const test = () =>
      withBusy(async () => {
        const installed = server.value;
        if (!installed) return;
        applyStatus(await controller.externalMcp.test(installed.id));
        await refresh();
      });

    const remove = () =>
      withBusy(async () => {
        const installed = server.value;
        if (!installed) return;
        await controller.externalMcp.remove(installed.id);
        errorText.value = null;
        await refresh();
      });

    // ── Display ──────────────────────────────────────────────────────────

    const status = computed(() => {
      const installed = server.value;
      if (!installed || !installed.enabled) {
        return { kind: "disabled", tooltip: "Server is disabled." } as const;
      }
      if (errorText.value) {
        return {
          kind: "error",
          tooltip: "Server failed to connect — see the error below.",
        } as const;
      }
      if ((installed.tools ?? []).length > 0) {
        return {
          kind: "healthy",
          tooltip: "Server is connected and its tools are available.",
        } as const;
      }
      return {
        kind: "unknown",
        tooltip: "No tools discovered yet — test the connection.",
      } as const;
    });

    const toolsSummary = computed(() => {
      const tools = server.value?.tools ?? [];
      if (tools.length === 0) return null;
      return `${tools.length} tool${tools.length === 1 ? "" : "s"} available: ${tools
        .map((tool) => tool.name)
        .join(", ")}`;
    });

    return {
      pkg,
      supported,
      loading,
      saving,
      busy,
      editing,
      errorText,
      server,
      fields,
      values,
      trust,
      trustNote,
      canSubmit,
      status,
      toolsSummary,
      openUrl,
      connect,
      startEdit,
      cancelEdit,
      save,
      setEnabled,
      test,
      remove,
      mcpTagEl,
      showMcpTooltip,
      hideMcpTooltip,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";

.mcp-tag {
  @extend %font-small-regular;
  align-self: center;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.03em;
  padding: 1px 6px;
  border-radius: $border-radius-m;
  white-space: nowrap;
  @include themed() {
    color: t($grey-60);
    background-color: t($grey-10);
  }
}

.docs-info {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: default;

  svg {
    width: 16px;
    height: 16px;
  }

  :deep(.stroke) {
    @include themed() {
      stroke: t($grey-40);
    }
  }

  &:hover :deep(.stroke) {
    @include themed() {
      stroke: t($primary);
    }
  }
}

.alert-container {
  width: 100%;
  padding: 16px 0;
}

// ── Empty phase (mirrors IntegrationSyncWorkflow) ───────────────────────────

.workflow-empty {
  padding: 8px 0;
}

.workflow-empty-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 20px;
  padding: 40px 32px;
  margin-top: 8px;
  border-radius: $border-radius-l;
  @include themed() {
    border: 1px solid t($grey-15);
    background-color: t($grey-03);
  }
}

.workflow-empty-logo-wrap {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  @include themed() {
    background-color: t($grey-05);
    box-shadow: 0 0 0 8px t($grey-03);
  }
}

.workflow-empty-logo {
  width: 32px;
  height: 32px;
  border-radius: $border-radius-m;
}

.workflow-empty-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.workflow-empty-headline {
  margin: 0;
  @extend %font-body-medium;
  @include themed() {
    color: t($primary);
  }
}

.workflow-empty-sub {
  margin: 0;
  @extend %font-body-regular;
  @include themed() {
    color: t($grey-50);
  }
}

// ── Ready phase (mirrors IntegrationSyncWorkflow) ───────────────────────────

.workflow-ready {
  padding: 8px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;

  h3 {
    margin-bottom: 0;
    margin-top: 24px;
  }
}

.workflow-account {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  margin-bottom: 8px;
  border-radius: $border-radius-m;
  @include themed() {
    border: 1px solid t($grey-10);
  }
}

.workflow-ready-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.workflow-ready-icon {
  width: 18px;
  height: 18px;
  border-radius: $border-radius-s;
}

.workflow-ready-text {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;

  .workflow-ready-name {
    @extend %font-body-regular;
  }
}

.workflow-ready-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  cursor: default;
  &.healthy {
    background-color: #10b981;
  }
  &.error {
    @include themed() {
      background-color: t($secondary-50);
    }
  }
  &.disabled,
  &.unknown {
    @include themed() {
      background-color: t($grey-30);
    }
  }
}

.workflow-ready-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.context-action {
  width: 100%;
}

.trust-pill {
  border-radius: 999px;
  padding: 2px 8px;
  @extend %font-caption-medium;
  @include themed() {
    background-color: t($grey-05);
    color: t($grey-60);
  }

  &.all {
    color: $starred;
  }
}

.mcp-tools {
  margin: 0;
  @extend %font-caption-regular;
  @include themed() {
    color: t($grey-60);
  }
}

// ── Credential form ─────────────────────────────────────────────────────────

.mcp-form {
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  width: 100%;
  max-width: 420px;
  text-align: start;

  label {
    display: flex;
    flex-direction: column;
    row-gap: 4px;
    @extend %font-caption-medium;

    .optional {
      @include themed() {
        color: t($grey-50);
      }
    }

    input,
    select {
      padding: 8px 10px;
      border-radius: $border-radius-m;
      font-family: inherit;
      @include themed() {
        border: 1px solid t($grey-10);
        background-color: t($grey-03);
        color: t($grey-90);
      }
    }
  }

  .hint {
    margin: 0;
    @extend %font-caption-regular;
    @include themed() {
      color: t($grey-60);
    }
  }

  .form-actions {
    display: flex;
    column-gap: 8px;
    margin-top: 4px;
  }
}

.form-error {
  margin: 0;
  @extend %font-caption-regular;
  color: $red;
  overflow-wrap: anywhere;
}
</style>
